import { ReactComponent as FailedIcon } from "assets/fail.svg";
import { ReactComponent as SuccessIcon } from "assets/success.svg";
import cn from "classnames";
import { GlobalContextStore } from "common/context/providers/globalProvider";
import GeneralButton from "components/common/Button";
import BaseModal, { BaseModalProps } from "components/common/Modal/BaseModal";
import {
  ReactElement,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { IGlobalProviderContext } from "../../../@types/common";
import { ReactComponent as CrossSvg } from "../../../assets/white-cross.svg";
import Styled from "./Styled";

import styledComponents from "styled-components";

import BgDesktopFailedSvg from "assets/bg-dialog-failed-desktop.svg";
import BgMobileFailedSvg from "assets/bg-dialog-failed-mobile.svg";
import BgDesktopSuccessSvg from "assets/bg-dialog-success-desktop.svg";
import BgMobileSuccessSvg from "assets/bg-dialog-success-mobile.svg";

import { useDeleteAccountViewModel } from "common/viewModels/User/DeleteAccountViewModel";
import { UserViewModelContext } from "common/viewModels/UserViewModel";
import { WindowSize } from "utils/responsive";

export enum DeleteAccountOpenState {
  CONFIRM_DELETE = "CONFIRM_DELETE",
  ENTER_REASON = "ENTER_REASON",
  SUCCESS_DELETE = "SUCCESS_DELETE",
  FAILED_DELETE = "FAILED_DELETE",
}

type DeleteAccountModalProps = BaseModalProps & {
  onClose: () => void;
};

const DeleteMyAccountModal = (props: DeleteAccountModalProps) => {
  const { globalDispatch } = useContext<IGlobalProviderContext>(
    GlobalContextStore as any
  );

  const { currentUser, refreshCurrentUser } = useContext(UserViewModelContext);

  const { onClose, ...modalProps } = props;
  const { t, i18n } = useTranslation("myBooking");
  const { requestDeleteAccount } = useDeleteAccountViewModel();

  const [deleteReason, setDeleteReason] = useState<string>("");
  const [loading, setLoading] = useState(false);

  const [openState, setLocalModalOpenState] = useState<DeleteAccountOpenState>(
    DeleteAccountOpenState.CONFIRM_DELETE
  );

  const [deleteMessage, setDeleteMessage] = useState<string>("");

  const cleanModal = useCallback(() => {
    // setLocalModalOpenState(null);
    setLoading(false);
    setLocalModalOpenState(DeleteAccountOpenState.CONFIRM_DELETE);
    setDeleteMessage("");
    onClose && onClose();
  }, [modalProps]);

  const isMobile = WindowSize() === "mobile";

  const ModalState: Record<
    DeleteAccountOpenState,
    { title?: string; body?: ReactElement; footer?: ReactElement }
  > = useMemo(
    () => ({
      [DeleteAccountOpenState.CONFIRM_DELETE]: {
        title: t("profile:deleteAccount.deleteConfirmation"),
        body: (
          <>
            <div className="flex flex-col gap items-center text-app-blue font-medium">
              <div>{currentUser?.memberId}</div>
              <div>{currentUser?.username}</div>
              <div>{currentUser?.email}</div>
              <div>{currentUser?.phoneNum}</div>
            </div>
            <div className="mt-6 text-center">
              {t("profile:deleteAccount.deleteConfirmationMsg")}
            </div>
          </>
        ),
        footer: (
          <GeneralButton
            onClick={() =>
              setLocalModalOpenState(DeleteAccountOpenState.ENTER_REASON)
            }
          >
            {t("profile:deleteAccount.deleteConfirmationBtn")}
          </GeneralButton>
        ),
      },
      [DeleteAccountOpenState.ENTER_REASON]: {
        title: t("profile:deleteAccount.deleteAccountReason"),
        body: (
          <>
            {/**reason dialog */}
            <Styled.TextArea
              className="w-full border-grey-text-100 border-2 rounded-lg"
              value={deleteReason}
              onChange={(e) => setDeleteReason(e.target.value)}
              placeholder={t(
                "profile:deleteAccount.deleteAccountReasonPlaceholder"
              )}
            />
          </>
        ),
        footer: (
          <GeneralButton
            disabled={loading}
            onClick={async () =>
              // call api
              {
                setLoading(true);
                try {
                  const res = await requestDeleteAccount({
                    reason: deleteReason,
                  });
                  if (res.success) {
                    setLocalModalOpenState(
                      DeleteAccountOpenState.SUCCESS_DELETE
                    );
                    setDeleteMessage(res?.message);
                  } else {
                    setLocalModalOpenState(
                      DeleteAccountOpenState.FAILED_DELETE
                    );
                    setDeleteMessage(res?.message);
                  }
                } catch (error) {
                  setLocalModalOpenState(DeleteAccountOpenState.FAILED_DELETE);
                }
                refreshCurrentUser();
                setLoading(false);
              }
            }
          >
            {t("common:button.submit")}
          </GeneralButton>
        ),
      },
      [DeleteAccountOpenState.SUCCESS_DELETE]: {
        body: (
          <div
            className={cn(
              // Base styles
              "flex flex-col items-center gap-4 pt-8",
              // MD breakpoint styles
              "md:flex-row md:pt-16 md:gap-11 md:px-20"
            )}
          >
            <SuccessIcon className="flex-shrink-0" />
            <div className={cn("flex md:items-start flex-col gap-4")}>
              {" "}
              {
                <Styled.Title
                  style={{ textAlign: isMobile ? "center" : "start" }}
                >
                  {t("profile:deleteAccount.deleteSuccessTitle")}
                </Styled.Title>
              }
              <div
                className={cn(
                  // Base styles
                  "h-32 overflow-y-auto text-center",
                  // MD breakpoint styles
                  "md:h-auto md:max-h-40 md:text-left"
                )}
              >
                {deleteMessage}
              </div>
              {!isMobile && (
                <GeneralButton
                  onClick={() => onClose && onClose()}
                  style={{
                    width: "250px",
                  }}
                >
                  {t("common:button.ok")}
                </GeneralButton>
              )}
            </div>
          </div>
        ),
        footer: isMobile ? (
          <GeneralButton onClick={() => onClose && onClose()}>
            {t("common:button.ok")}
          </GeneralButton>
        ) : undefined,
      },
      [DeleteAccountOpenState.FAILED_DELETE]: {
        body: (
          <div
            className={cn(
              // Base styles
              "flex flex-col items-center gap-4 pt-8",
              // MD breakpoint styles
              "md:flex-row md:pt-16 md:gap-11 md:px-20"
            )}
          >
            <FailedIcon className="flex-shrink-0" />
            <div className={cn("flex md:items-start flex-col gap-4")}>
              {
                <Styled.Title>
                  {t("profile:deleteAccount.deleteFailedTitle")}
                </Styled.Title>
              }
              <div
                className={cn(
                  // Base styles
                  "h-32 overflow-y-auto text-center",
                  // MD breakpoint styles
                  "md:h-auto md:max-h-40 md:text-left"
                )}
              >
                {deleteMessage}
              </div>
              {!isMobile && (
                <GeneralButton
                  onClick={() =>
                    setLocalModalOpenState(DeleteAccountOpenState.ENTER_REASON)
                  }
                  style={{
                    width: "250px",
                  }}
                >
                  {t("common:button.retry")}
                </GeneralButton>
              )}
            </div>
          </div>
        ),
        footer: isMobile ? (
          <GeneralButton
            onClick={() =>
              setLocalModalOpenState(DeleteAccountOpenState.ENTER_REASON)
            }
          >
            {t("common:button.retry")}
          </GeneralButton>
        ) : undefined,
      },
    }),
    [t, deleteReason, currentUser, i18n, isMobile]
  );

  const styledByOpenState = {
    [DeleteAccountOpenState.CONFIRM_DELETE]: {
      maxWidth: "480px",
      minHeight: "480px",
    },
    [DeleteAccountOpenState.ENTER_REASON]: {
      maxWidth: "480px",
      minHeight: "480px",
    },
    [DeleteAccountOpenState.SUCCESS_DELETE]: {
      backgroundColor: "#F0FAFF",
      maxWidth: isMobile ? "340px" : "840px",
      minHeight: isMobile ? "600px" : "480px",
      backgroundImage: isMobile
        ? `url(${BgMobileSuccessSvg})`
        : `url(${BgDesktopSuccessSvg})`,
      backgroundPosition: isMobile ? "-100px 200px" : "bottom",
      backgroundSize: isMobile ? "cover" : "contain",
      backgroundRepeat: "no-repeat",
    },
    [DeleteAccountOpenState.FAILED_DELETE]: {
      maxWidth: isMobile ? "340px" : "840px",
      minHeight: isMobile ? "600px" : "480px",

      backgroundImage: isMobile
        ? `url(${BgMobileFailedSvg})`
        : `url(${BgDesktopFailedSvg})`,
      backgroundPosition: isMobile ? "-100px 200px" : "bottom",
      backgroundSize: isMobile ? "cover" : "contain",
      backgroundRepeat: "no-repeat",
    },
  };

  return (
    <BaseModal {...modalProps}>
      <Styled.CustomModalCard style={styledByOpenState[openState]}>
        <div
          className={cn(
            "w-full flex flex-col items-center h-full px-8 md:px-12 py-12 gap-9",
            "relative z-10"
          )}
        >
          {ModalState[openState]?.title && (
            <Styled.Title>{ModalState[openState].title}</Styled.Title>
          )}
          <Styled.ModalBody
            className={cn({
              "w-full": true,
            })}
          >
            {openState && ModalState[openState]?.body}
          </Styled.ModalBody>
          {ModalState[openState]?.footer && (
            <div
              className={cn(
                "flex flex-col",
                "lg:justify-center",
                "button-group",
                "gap-4",
                "w-full"
              )}
            >
              {openState && ModalState[openState]?.footer}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={cleanModal}
          className={cn({
            "absolute top-[-36.04px] right-0 lg:top-0 lg:right-[-42px]": [
              DeleteAccountOpenState.CONFIRM_DELETE,
              DeleteAccountOpenState.ENTER_REASON,
            ].includes(openState),
            "absolute top-11 left-14 lg:top-8 lg:left-14 z-20": [
              DeleteAccountOpenState.FAILED_DELETE,
              DeleteAccountOpenState.SUCCESS_DELETE,
            ].includes(openState),
          })}
        >
          <StyledCrossSvg state={openState} />
        </button>
      </Styled.CustomModalCard>
    </BaseModal>
  );
};

const StyledCrossSvg = styledComponents(CrossSvg)<{
  state: DeleteAccountOpenState;
}>`
  path {
    fill: ${(props) =>
      [
        DeleteAccountOpenState.FAILED_DELETE,
        DeleteAccountOpenState.SUCCESS_DELETE,
      ].includes(props.state)
        ? "black"
        : "white"}
  }
`;

export default DeleteMyAccountModal;
